import { ChartWidget } from '@organization/features/dashboard/types'

import { lazyRechartsComponent } from '@organization/features/dashboard/utils'
import { hashCode, intToRGB } from '@organization/utils'
import { Card, Flex } from 'antd'
import { ArrowRight, Chart } from 'iconsax-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useStyle } from './styles'

export const Area = lazyRechartsComponent('Area')
export const AreaChart = lazyRechartsComponent('AreaChart')
export const XAxis = lazyRechartsComponent('XAxis')
export const YAxis = lazyRechartsComponent('YAxis')
export const ResponsiveContainer = lazyRechartsComponent('ResponsiveContainer')

interface Props {
  instance: ChartWidget
}

interface DataKeysProps {
  value: string
  color: string
}

export const DashboardCharts = ({ instance }: Props) => {
  const [data] = useState(instance.chart)
  const [dataKeys, setDataKeys] = useState<DataKeysProps[]>([])
  const [xKey, setXKey] = useState<string>()
  const [yKey, setYKey] = useState<string>()

  const { t } = useTranslation()
  const { styles } = useStyle()

  useEffect(() => {
    const keys: DataKeysProps[] = []

    for (const key in data[0]) {
      keys.push({ value: key, color: intToRGB(hashCode(key)) })
    }

    if (keys[0]) {
      setXKey(keys[0].value)
      setYKey(keys[keys.length - 1].value)

      setDataKeys(keys)
    }
  }, [data, setDataKeys])

  return (
    <Card
      className={styles.dashboardCard}
      title={
        <Flex align="center" gap={8}>
          <Chart size={18} />
          <div>{instance.title}</div>
        </Flex>
      }
      extra={
        <Link to={instance.redirect_url} target="_blank">
          <Flex gap={8} align="center">
            {t('common.view_all')}
            <ArrowRight size={16} />
          </Flex>
        </Link>
      }
    >
      <ResponsiveContainer minHeight="375px">
        <AreaChart data={data}>
          <defs>
            {dataKeys.map(({ value, color }: DataKeysProps) => (
              <linearGradient
                key={value}
                id={value}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <XAxis dataKey={xKey} axisLine={false} tickLine={false} />
          <YAxis dataKey={yKey} width={25} axisLine={false} tickLine={false} />
          {dataKeys.map(({ value, color }: DataKeysProps) => (
            <Area
              key={value}
              type="monotone"
              dataKey={value}
              stroke={color}
              fillOpacity={1}
              fill={`url(#${value})`}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  )
}
